import { useTranslation } from 'react-i18next';

import { Badge } from '@codes/ui-kit/components/ui/badge';
import { Button } from '@codes/ui-kit/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@codes/ui-kit/components/ui/card';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@codes/ui-kit/components/ui/tooltip';
import { format, formatDistanceToNow } from 'date-fns';
import { KeyRoundIcon, TrashIcon } from 'lucide-react';

import type { SecretCredentialView } from '@codes/access-keys';
import { cn } from '@codes/ui-kit';

import { CopyButton } from './CopyButton';

interface AccessKeyCardProps {
    data: SecretCredentialView;
    onEdit: (key: SecretCredentialView) => void;
    onDelete: (key: SecretCredentialView) => void;
}

const formatExpiration = (expiredAt: number | undefined): string => {
    if (!expiredAt) {
        return 'No expiration';
    }
    const expirationDate = new Date(expiredAt);
    const now = new Date();
    if (expirationDate < now) {
        return 'Expired';
    }
    return formatDistanceToNow(expirationDate, { addSuffix: true });
};

export const AccessKeyCard = ({ data, onEdit, onDelete }: AccessKeyCardProps) => {
    const { t } = useTranslation();

    const isExpired = data.expiredAt && new Date(data.expiredAt) < new Date();

    return (
        <Card className="group transition-all hover:shadow-md hover:border-point">
            <CardHeader className="p-4 pb-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <CardTitle className="text-base font-semibold truncate mb-1">
                            {data.userName || data.id || 'Unnamed Credential'}
                        </CardTitle>
                        {data.createdAt && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>
                                    {t('accessKeys.list.createdAt')}:{' '}
                                    {formatDistanceToNow(new Date(data.createdAt), { addSuffix: true })}
                                </span>
                            </div>
                        )}
                    </div>
                    {data.expiredAt && (
                        <Badge
                            variant={isExpired ? 'secondary' : 'default'}
                            className="flex items-center gap-1 shrink-0"
                        >
                            <div className={cn('h-2 w-2 rounded-full', isExpired ? 'bg-red-500' : 'bg-green-500')} />
                            {formatExpiration(data.expiredAt)}
                        </Badge>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
                <div className="bg-muted/50 rounded-md p-2">
                    <div className="flex items-center gap-2">
                        <KeyRoundIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <Input
                            value={data.accessKey}
                            readOnly
                            className="flex-1 h-7 font-mono text-xs border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 px-2"
                            type="text"
                        />
                        <CopyButton value={data.accessKey} />
                    </div>
                </div>

                {data.arn && (
                    <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                        <span className="font-medium">ARN:</span> {data.arn}
                    </p>
                )}

                <div className="flex items-center justify-between text-xs pt-2">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help text-muted-foreground">
                                    {data.expiredAt ? `Expires: ${formatExpiration(data.expiredAt)}` : 'No expiration'}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>
                                    {data.expiredAt
                                        ? format(new Date(data.expiredAt), 'PPpp')
                                        : 'This credential does not expire'}
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onEdit(data)}>
                            {t('accessKeys.form.edit')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => onDelete(data)}
                        >
                            <TrashIcon className="h-3 w-3" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
